import { oc } from "@orpc/contract";
import { z } from "zod";
import { FileExtractionOutputFormatSchema } from "@erp/shared-types";
import { SchemaDefinitionResponseSchema } from "../core/schema-definitions.contract";

const UUIDSchema = z.string().uuid();

const FileExtractionRunSummarySchema = z.object({
  id: UUIDSchema,
  schemaDefinitionId: UUIDSchema,
  schemaDefinitionVersion: z.number().int().positive(),
  schemaDefinitionVersionId: UUIDSchema,
  documentId: UUIDSchema,
  documentFileName: z.string(),
  ocrResultId: UUIDSchema,
  outputFormat: FileExtractionOutputFormatSchema,
  status: z.enum(["pending", "processing", "completed", "failed"]),
  error: z.string().nullable(),
  userId: UUIDSchema,
  userName: z.string().nullable(),
  createdAt: z.date(),
});

const FileExtractionRunDetailSchema = FileExtractionRunSummarySchema.extend({
  documentMimeType: z.string().nullable(),
  userInstructions: z.string().nullable(),
  extractedResult: z.record(z.string(), z.unknown()).nullable(),
  formattedOutput: z.string(),
  formattedRows: z.array(z.record(z.string(), z.unknown())),
});

export const fileExtractionContracts = {
  listSchemas: oc
    .input(z.object({}))
    .output(z.array(SchemaDefinitionResponseSchema))
    .route({
      method: "GET",
      summary: "List file extraction schemas",
      description:
        "Lists the active extraction schemas the current user can use to pull structured data out of uploaded documents.",
    }),

  listRuns: oc
    .input(
      z.object({
        schemaDefinitionId: UUIDSchema,
        limit: z.number().int().positive().max(200).optional(),
        offset: z.number().int().nonnegative().optional(),
      }),
    )
    .output(
      z.object({
        items: z.array(FileExtractionRunSummarySchema),
        total: z.number().int().nonnegative(),
      }),
    )
    .route({
      method: "GET",
      summary: "List file extraction runs",
      description:
        "Pages through past extraction runs for one extraction schema: document name, schema version, CSV or JSON output format, status, error and who ran it.",
    }),

  getRun: oc
    .input(z.object({ runId: UUIDSchema }))
    .output(FileExtractionRunDetailSchema)
    .route({
      method: "GET",
      summary: "Get a file extraction run",
      description:
        "Returns one extraction run with the document, instructions, raw extracted fields, and the result formatted as CSV or JSON plus table rows.",
    }),

  run: oc
    .input(
      z.object({
        schemaDefinitionId: UUIDSchema,
        documentId: UUIDSchema,
        userInstructions: z.string().nullable().optional(),
        outputFormat: FileExtractionOutputFormatSchema.optional(),
      }),
    )
    .output(FileExtractionRunDetailSchema)
    .route({
      method: "POST",
      summary: "Extract data from a document",
      description:
        "Runs a published extraction schema over one of the user's uploaded documents, with optional instructions, records the run, and returns the extracted data as CSV or JSON.",
    }),
} as const;

export type FileExtractionRunSummary = z.infer<
  typeof FileExtractionRunSummarySchema
>;
export type FileExtractionRunDetail = z.infer<
  typeof FileExtractionRunDetailSchema
>;
